/**
 * Admin Orders List Page
 * Complete order management with filtering and actions
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/client';
import DataTable, { type Column } from '../../../components/ui/DataTable';
import Pagination from '../../../components/ui/Pagination';
import Badge from '../../../components/ui/Badge';
import type { OrderWithItems, OrderStats } from '@phoenixbill/shared/types/order';

export default function OrdersListPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(20);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Sorting
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadOrders();
  }, [currentPage, search, statusFilter, dateFrom, dateTo, sortBy, sortOrder]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
      };

      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.dateFrom = new Date(dateFrom).toISOString();
      if (dateTo) params.dateTo = new Date(dateTo).toISOString();

      const response = await api.get('/admin/orders', { params });
      const data = response.data.data;

      setOrders(data.orders);
      setTotalPages(data.pagination.totalPages);
      setTotalItems(data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/orders/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleRowClick = (order: OrderWithItems) => {
    navigate(`/admin/orders/${order.id}`);
  };

  const formatPrice = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'success' | 'warning' | 'danger' | 'default'; label: string }> = {
      pending: { variant: 'warning', label: 'Pending' },
      active: { variant: 'success', label: 'Active' },
      fraud: { variant: 'danger', label: 'Fraud' },
      cancelled: { variant: 'default', label: 'Cancelled' },
    };
    const config = statusMap[status] || statusMap.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPaymentBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'success' | 'warning' | 'danger' | 'default'; label: string }> = {
      unpaid: { variant: 'warning', label: 'Unpaid' },
      paid: { variant: 'success', label: 'Paid' },
      refunded: { variant: 'default', label: 'Refunded' },
    };
    const config = statusMap[status] || statusMap.unpaid;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const columns: Column<OrderWithItems>[] = [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      render: (order) => <span className="font-mono text-sm">#{order.id}</span>,
    },
    {
      key: 'orderNumber',
      label: 'Order Number',
      sortable: true,
      render: (order) => <span className="font-mono text-sm">{order.orderNumber}</span>,
    },
    {
      key: 'client',
      label: 'Client',
      render: (order) => (
        <div>
          <div className="font-medium">
            {order.firstName} {order.lastName}
          </div>
          <div className="text-xs text-slate-500">{order.email}</div>
        </div>
      ),
    },
    {
      key: 'items',
      label: 'Items',
      render: (order) => <span className="text-sm">{order.items.length} item(s)</span>,
    },
    {
      key: 'total',
      label: 'Total',
      sortable: true,
      render: (order) => <span className="font-semibold">{formatPrice(order.total)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (order) => getStatusBadge(order.status),
    },
    {
      key: 'paymentStatus',
      label: 'Payment',
      sortable: true,
      render: (order) => getPaymentBadge(order.paymentStatus),
    },
    {
      key: 'createdAt',
      label: 'Date',
      sortable: true,
      render: (order) => (
        <span className="text-sm">{new Date(order.createdAt).toLocaleDateString()}</span>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-100">Order Management</h1>
        <p className="text-slate-400 mt-1">Manage and track all customer orders</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Orders"
            value={stats.totalOrders.toString()}
            icon="📦"
            color="blue"
          />
          <StatCard
            title="Pending Orders"
            value={stats.pendingOrders.toString()}
            icon="⏳"
            color="yellow"
          />
          <StatCard
            title="Fraud Alerts"
            value={stats.fraudOrders.toString()}
            icon="⚠️"
            color="red"
          />
          <StatCard
            title="Total Revenue"
            value={formatPrice(stats.totalRevenue)}
            icon="💰"
            color="green"
          />
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Order #, email, name..."
              className="w-full px-3 py-2 bg-gray-900 border border-slate-700/50 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 bg-gray-900 border border-slate-700/50 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="fraud">Fraud</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 bg-gray-900 border border-slate-700/50 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 bg-gray-900 border border-slate-700/50 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>

        {/* Clear Filters */}
        {(search || statusFilter || dateFrom || dateTo) && (
          <div className="mt-4">
            <button
              onClick={() => {
                setSearch('');
                setStatusFilter('');
                setDateFrom('');
                setDateTo('');
                setCurrentPage(1);
              }}
              className="text-sm text-orange-400 hover:text-orange-300"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50">
        <DataTable
          data={orders}
          columns={columns}
          keyField="id"
          loading={loading}
          onSort={handleSort}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onRowClick={handleRowClick}
        />

        {!loading && orders.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-400">No orders found</p>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <div className="border-t border-slate-700/50 px-6 py-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string;
  icon: string;
  color: 'blue' | 'green' | 'yellow' | 'red';
}

function StatCard({ title, value, icon, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-orange-900/20 border-orange-800 text-orange-400',
    green: 'bg-green-900/20 border-green-800 text-green-400',
    yellow: 'bg-yellow-900/20 border-yellow-800 text-yellow-400',
    red: 'bg-red-900/20 border-red-800 text-red-400',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="text-2xl font-bold mb-1">{value}</div>
      <div className="text-sm opacity-80">{title}</div>
    </div>
  );
}
